<template>
	<div
		ref="listRef"
		class="relative flex items-center w-full md:w-[calc(20%-20px)] min-h-[56px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-2 md:pb-0 mt-3 md:mt-0 border-b-[1px] border-gray-light md:border-0"
	>
		<div @click="showList = !showList" class="flex-col w-[calc(100%-30px)] cursor-pointer">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("jobs.searchbar.location.title") }}</span>
			<input
				v-if="canType || selectedLocations.length == 0"
				ref="inputRef"
				v-model="inputVmodel"
				class="w-full outline-none border-none bg-transparent"
				type="text"
				:placeholder="placeholder"
			/>
			<span
				v-else
				@click="canType = true"
				class="block overflow-hidden overflow-ellipsis whitespace-nowrap w-[calc(100%-6px)] cursor-default"
			>
				{{ selectedLocations.map((location) => location.name[locale]).join(", ") }}
			</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-show="showList"
			class="absolute p-5 left-0 md:-left-8 top-[60px] md:top-[85px] w-[400px] max-w-[100%] md:max-w-[100vw] max-h-[350px] bg-white border border-dj-gray-light rounded-big flex flex-col z-30"
		>
			<client-only>
				<h3 class="font-medium mb-[10px]">{{ $t("mainSearchbar.location.results") }}</h3>
				<div class="flex flex-col gap-y-[10px] overflow-auto">
					<div v-if="false && (getCurrentRegionLocation.country.showCountry || inputVmodel.length < 1)">
						{{ getCurrentRegionLocation.country.name[locale] }}
					</div>
					<template v-for="(location, index) in getCurrentRegionLocation.list" :key="`region-${index}`">
						<BaseCheckbox
							v-show="location.showLocation || inputVmodel.length < 1"
							v-model="location.value"
							@change="onChange($event, location)"
						>
							{{ location.name[locale] }}
						</BaseCheckbox>
					</template>

					<template v-for="(country, index) in getOtherLocations" :key="`other-${index}`">
						<div v-if="false && (country.showCountry || inputVmodel.length < 1)">
							{{ country.name[locale] }}
						</div>
						<template v-for="(location, ind) in country.children" :key="`other-${index}-${ind}`">
							<BaseCheckbox
								v-show="location.showLocation || inputVmodel.length < 1"
								v-model="location.value"
								@change="onChange($event, location)"
							>
								{{ location.name[locale] }}
							</BaseCheckbox>
						</template>
					</template>
				</div>
			</client-only>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType, computed } from "vue";
import { onClickOutside, useFocus } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { type locationObject } from "@@/types";

export default defineComponent({
	name: "SearchbarJobsLocation",
	props: {
		modelValue: {
			type: Array as PropType<locationObject[]>,
			required: true,
		},
		filterList: {
			type: Array as PropType<string[]>,
			default: [],
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue", "loaded"],
	async setup(props, { emit }) {
		const { locale } = useI18n();
		const region = ref("");
		const inputRef = ref(null);
		const listRef = ref(null);
		const inputVmodel = ref("");
		const locationsData = ref([]);
		const locationsWithValue = ref([]);
		const selectedLocations = ref<locationObject[]>([]);
		const showList = ref(false);
		const canType = ref(false);
		const route = useRoute();

		onMounted(() => {
			hydrateSelectedLocations();

			emit("loaded");
		});

		function setSelectedLocationsByIds(selectedIds: number[]) {
			selectedLocations.value = [];
			locationsWithValue.value.forEach((country, index) => {
				locationsWithValue.value[index].children.forEach((child, childIndex) => {
					const isSelected = selectedIds.includes(Number(child.id));
					locationsWithValue.value[index].children[childIndex].value = isSelected;
					if (isSelected) {
						selectedLocations.value.push(locationsWithValue.value[index].children[childIndex]);
					}
				});
			});
		}

		const flatLocations = computed(() => locationsWithValue.value.flatMap((country) => country.children ?? []));

		function getLocationIdBySlug(targetSlug) {
			// 1. Try to find an exact match first (e.g., "cluj-napoca")
			let match = flatLocations.value.find((loc) => loc.slug === targetSlug);

			// 2. If not found, check for the numeric suffix (e.g., "cluj-napoca-1")
			if (!match) {
				const safeSlug = targetSlug.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
				const regex = new RegExp(`^${safeSlug}(-\\d+)?$`);
				match = flatLocations.value.find((loc) => regex.test(loc.slug));
			}

			return match ? Number(match.id) : null;
		}

		function getValuesFromUrl() {
			region.value = useRegion.getCurrentRegion().code == "HU" ? "hungary" : "romania";

			// 1. Check for the SEO slug in the route parameters first (e.g., /jobs/cluj-napoca)
			if (route.params.citySlug) {
				const locationId = getLocationIdBySlug(route.params.citySlug);

				if (locationId) {
					setSelectedLocationsByIds([locationId]);
					emit("update:modelValue", selectedLocations.value);
					return; // Exit early since we found the location via the slug
				}
			}

			// 2. Fallback to the old query parameter logic (e.g., ?location=1,2,3)
			const query = route.query;
			if (query.location) {
				const queryLocations = query.location
					.toString()
					.split(",")
					.map((ql) => Number.parseInt(ql))
					.filter((id) => !Number.isNaN(id));
				setSelectedLocationsByIds(queryLocations);
				emit("update:modelValue", selectedLocations.value);
			}
		}

		function hydrateSelectedLocations() {
			getValuesFromUrl();

			if (!route.query.location && props.modelValue.length > 0) {
				const modelValueIds = props.modelValue
					.map((location) => Number(location.id))
					.filter((id) => !Number.isNaN(id));

				if (modelValueIds.length > 0) {
					setSelectedLocationsByIds(modelValueIds);
				}
			}
		}

		watch(
			() => props.modelValue,
			(value) => {
				if (value.length == 0) {
					selectedLocations.value = [];
					// clear selected values
					locationsWithValue.value.forEach((location) => {
						location.children.forEach((child) => {
							if (child.value) child.value = false;
						});
					});
				} else if (!route.query.location) {
					const modelValueIds = value
						.map((location) => Number(location.id))
						.filter((id) => !Number.isNaN(id));
					setSelectedLocationsByIds(modelValueIds);
				}
			}
		);

		if (props.filterList.length > 0) {
			locationsData.value = props.filterList;
			setDataArrays();
		}
		function setDataArrays() {
			locationsWithValue.value = transformArray();
		}
		function transformArray() {
			return sortArrayByObjectByLocale(locationsData.value, "name", locale.value).value.map((location) => {
				return {
					...location,
					value: false,
				};
			});
		}
		const getCurrentRegionLocation = computed(() => {
			const country = locationsWithValue.value.filter((country) => country.slug == region.value)[0];
			const list = sortArrayByObjectByLocale(country.children, "name", locale.value).value;
			return { list: list, country: country };
		});
		const getOtherLocations = computed(() => {
			const otherLocations = locationsWithValue.value.filter((country) => country.slug != region.value);
			otherLocations.forEach((el, index) => {
				otherLocations[index].children = sortArrayByObjectByLocale(el.children, "name", locale.value).value;
			});

			return otherLocations;
		});
		watch(inputVmodel, () => {
			if (inputVmodel.value.length > 0) {
				const pattern = new RegExp(inputVmodel.value, "gi");
				locationsWithValue.value.forEach((country, index) => {
					let showThisCountry = false;
					country.children.forEach((location, ind) => {
						if (location.name[locale.value].match(pattern)) {
							locationsWithValue.value[index].children[ind].showLocation = true;
							showThisCountry = true;
						} else {
							locationsWithValue.value[index].children[ind].showLocation = false;
						}
					});
					locationsWithValue.value[index].showCountry = showThisCountry;
				});

				showList.value = true;
			} else {
				locationsWithValue.value.forEach((country, index) => {
					country.children.forEach((location, ind) => {
						locationsWithValue.value[index].children[ind].showLocation = true;
					});
					locationsWithValue.value[index].showCountry = true;
				});
			}
		});
		const { focused: inputFocus } = useFocus(inputRef);
		onClickOutside(listRef, () => {
			showList.value = false;
			inputVmodel.value = "";
		});
		onClickOutside(inputRef, () => {
			canType.value = false;
		});
		function onChange(event, settlement: locationObject) {
			event
				? selectedLocations.value.push(settlement)
				: selectedLocations.value.splice(selectedLocations.value.indexOf(settlement), 1);
			emit("update:modelValue", selectedLocations.value);
		}

		function getSelectedLocations() {
			return selectedLocations.value.map((location) => {
				return location.name[locale.value];
			});
		}

		// Check the url parameters and set value if given
		watch(
			() => route.query,
			(value) => {
				if (value && value.location != undefined) {
					getValuesFromUrl();
				}
			}
		);

		return {
			inputRef,
			inputVmodel,
			inputFocus,
			listRef,
			showList,
			onChange,
			selectedLocations,
			canType,
			locale,
			region,
			getCurrentRegionLocation,
			getOtherLocations,
			sortArrayByObjectByLocale,
			getSelectedLocations,
		};
	},
});
</script>
